/**
 * Case runs and the `case_worker` canvas job (W3-07, frontend half).
 *
 * Pure helpers shared by the case page, the case canvas, the case report and the
 * canvas operator rail:
 *
 *  - `watchCanvasJob` polls `GET /canvas/{case_id}/jobs/{job_id}` until the job
 *    ends (succeeded / failed / cancelled). It chains timeouts, so a slow answer
 *    never overlaps the next request, and its stop function silences every
 *    callback that is still in flight.
 *  - `startAutoInvestigateRun` starts the case's Auto-Investigate run as a
 *    background job (`?async=1`) and falls back to the synchronous request only
 *    when the async start failed for a reason the synchronous path does not share
 *    (no answer, 5xx). A 400 (no seeds), 403/404 (no access) or 409 (a run is
 *    already going) is reported, never retried synchronously.
 *  - Formatting for run rows and job progress.
 *
 * The service calls are injected, so this module does not import the API client.
 */
import type { CanvasJobEnvelope, CanvasJobProgress, CanvasJobView, CaseRun } from '../../services/api';

export const JOB_POLL_MS = 5000;
export const TERMINAL_JOB_STATUSES = ['succeeded', 'failed', 'cancelled'] as const;
const ACTIVE_JOB_STATUSES = ['queued', 'running'] as const;

export function isTerminalJobStatus(status: unknown): boolean {
    return (TERMINAL_JOB_STATUSES as readonly string[]).includes(String(status || '').toLowerCase());
}

export function isActiveJobStatus(status: unknown): boolean {
    return (ACTIVE_JOB_STATUSES as readonly string[]).includes(String(status || '').toLowerCase());
}

/** A run whose job is still queued or running. */
export function isRunActive(run: Pick<CaseRun, 'status' | 'job_status'>): boolean {
    return isActiveJobStatus(run.job_status) || isActiveJobStatus(run.status);
}

/** The newest run that is still going and carries a job id (resume target after a reload). */
export function findActiveRun(runs: CaseRun[] | null | undefined): CaseRun | null {
    return (runs || []).find((r) => !!r.job_id && isRunActive(r)) || null;
}

// ── Polling ──────────────────────────────────────────────────────────────────

export interface WatchCanvasJobOptions {
    caseId: string;
    jobId: string;
    fetchJob: (caseId: string, jobId: string) => Promise<CanvasJobView>;
    intervalMs?: number;
    /** Consecutive transient failures tolerated before the watch gives up. */
    maxConsecutiveErrors?: number;
    onUpdate?: (job: CanvasJobView) => void;
    /** Called once, when the job reaches a terminal status. */
    onDone?: (job: CanvasJobView) => void;
    /** `fatal` is true when the watch stopped (job not found, no access, too many failures). */
    onError?: (message: string, fatal: boolean) => void;
}

function httpStatus(err: unknown): number | undefined {
    return (err as { response?: { status?: number } } | null)?.response?.status;
}

function errorText(err: unknown): string {
    const e = err as { response?: { data?: { detail?: unknown } }; message?: string } | null;
    const detail = e?.response?.data?.detail;
    if (typeof detail === 'string' && detail) return detail;
    if (detail && typeof detail === 'object' && typeof (detail as { message?: unknown }).message === 'string') {
        return String((detail as { message: string }).message);
    }
    return e?.message || 'request failed';
}

/**
 * Poll a canvas job until it ends. The first read is immediate; later reads run
 * `intervalMs` after the previous answer. Returns a stop function.
 */
export function watchCanvasJob(opts: WatchCanvasJobOptions): () => void {
    const interval = opts.intervalMs ?? JOB_POLL_MS;
    const maxErrors = opts.maxConsecutiveErrors ?? 6;
    let stopped = false;
    let timer: ReturnType<typeof setTimeout> | undefined;
    let failures = 0;

    const schedule = () => {
        if (!stopped) timer = setTimeout(() => { void tick(); }, interval);
    };

    const tick = async () => {
        if (stopped) return;
        try {
            const job = await opts.fetchJob(opts.caseId, opts.jobId);
            if (stopped) return;
            failures = 0;
            opts.onUpdate?.(job);
            if (isTerminalJobStatus(job?.status)) {
                stopped = true;
                opts.onDone?.(job);
                return;
            }
        } catch (err) {
            if (stopped) return;
            const status = httpStatus(err);
            if (status === 401 || status === 403 || status === 404) {
                stopped = true;
                opts.onError?.(status === 404 ? 'job not found' : errorText(err), true);
                return;
            }
            failures += 1;
            if (failures >= maxErrors) {
                stopped = true;
                opts.onError?.(`job status unavailable: ${errorText(err)}`, true);
                return;
            }
            opts.onError?.(errorText(err), false);
        }
        schedule();
    };

    void tick();
    return () => {
        stopped = true;
        if (timer !== undefined) clearTimeout(timer);
    };
}

// ── Starting a run ───────────────────────────────────────────────────────────

export type StartRunOutcome =
    | { mode: 'job'; job: CanvasJobEnvelope }
    /**
     * `fallback`: the async start failed and `runSync` ran the synchronous request.
     * `inline`: the server answered the async call with a finished run (no job).
     */
    | { mode: 'sync'; result: unknown; via: 'fallback' | 'inline' };

/** Errors the synchronous request would hit too: report them instead of retrying. */
const NO_FALLBACK_STATUSES = new Set([400, 401, 403, 404, 409, 422]);

export function shouldFallBackToSync(err: unknown): boolean {
    const status = httpStatus(err);
    return status === undefined || !NO_FALLBACK_STATUSES.has(status);
}

/**
 * Start the case's Auto-Investigate run. `startJob` is the `?async=1` call;
 * `runSync` is the synchronous request, used only when the async start failed
 * with no answer or a server error (and when an older server answered the async
 * call with a finished investigation file instead of a job).
 */
export async function startAutoInvestigateRun(
    startJob: () => Promise<CanvasJobEnvelope>,
    runSync: () => Promise<unknown>,
): Promise<StartRunOutcome> {
    let started: CanvasJobEnvelope | null = null;
    try {
        started = await startJob();
    } catch (err) {
        if (!shouldFallBackToSync(err)) throw err;
        return { mode: 'sync', result: await runSync(), via: 'fallback' };
    }
    if (started && typeof started === 'object' && typeof started.job_id === 'string' && started.job_id) {
        return { mode: 'job', job: started };
    }
    return { mode: 'sync', result: started, via: 'inline' };
}

/** One readable line for an error from a start or a run. */
export function runErrorMessage(err: unknown): string {
    return errorText(err);
}

// ── Formatting ───────────────────────────────────────────────────────────────

const COUNT_LABELS: Record<string, string> = {
    seeds: 'seeds',
    crypto_addresses: 'crypto addresses',
    phones: 'phones',
    emails: 'emails',
    upi_ids: 'UPI IDs',
    transactions: 'transactions',
    flows: 'flows',
    graph_wallets: 'graph wallets',
    seizure_targets: 'seizure targets',
    passes_run: 'passes run',
    spine_entities: 'case entities',
    spine_edges: 'case edges',
    nodes: 'nodes',
    edges: 'edges',
};

/**
 * The run's counts accumulate step by step (seeds first, graph counts last), so
 * the most informative ones are shown first rather than the earliest ones.
 */
const COUNT_PRIORITY = [
    'nodes', 'edges', 'spine_entities', 'spine_edges', 'seizure_targets', 'flows',
    'transactions', 'graph_wallets', 'crypto_addresses', 'phones', 'upi_ids', 'seeds',
];

const humanKey = (k: string) => COUNT_LABELS[k] || k.replace(/_/g, ' ');

const countValue = (v: unknown): number =>
    typeof v === 'number' ? v : (typeof v === 'string' && v.trim() !== '' ? Number(v) : NaN);

/** "120 nodes · 340 transactions": numeric counts only, most informative first. */
export function formatProgressCounts(counts: CanvasJobProgress['counts'], max = 4): string {
    if (!counts || typeof counts !== 'object') return '';
    const keys = Object.keys(counts);
    const ordered = [
        ...COUNT_PRIORITY.filter((k) => keys.includes(k)),
        ...keys.filter((k) => !COUNT_PRIORITY.includes(k)),
    ];
    const parts: string[] = [];
    for (const k of ordered) {
        const n = countValue((counts as Record<string, unknown>)[k]);
        if (!Number.isFinite(n)) continue;
        parts.push(`${n.toLocaleString('en-IN')} ${humanKey(k)}`);
        if (parts.length >= max) break;
    }
    return parts.join(' · ');
}

/** "Step 4/16 · Deep per-seed investigation · 12 crypto addresses", or '' when nothing is known yet. */
export function describeJobProgress(progress: CanvasJobProgress | null | undefined): string {
    if (!progress || typeof progress !== 'object') return '';
    const parts: string[] = [];
    if (typeof progress.step_index === 'number' && typeof progress.total_steps === 'number' && progress.total_steps > 0) {
        parts.push(`Step ${progress.step_index}/${progress.total_steps}`);
    }
    const label = (typeof progress.message === 'string' && progress.message.trim())
        || (typeof progress.step === 'string' && progress.step.trim() ? progress.step.replace(/_/g, ' ') : '');
    if (label) parts.push(label);
    const counts = formatProgressCounts(progress.counts);
    if (counts) parts.push(counts);
    return parts.join(' · ');
}

export function formatDurationSeconds(sec: number | null | undefined): string {
    if (typeof sec !== 'number' || !Number.isFinite(sec) || sec < 0) return '—';
    const s = Math.round(sec);
    const h = Math.floor(s / 3600);
    const m = Math.floor((s % 3600) / 60);
    const r = s % 60;
    if (h) return `${h}h ${String(m).padStart(2, '0')}m`;
    if (m) return `${m}m ${String(r).padStart(2, '0')}s`;
    return `${r}s`;
}

/** Seconds since an ISO timestamp (0 when absent or unparseable). */
export function secondsSince(iso: string | null | undefined, now = Date.now()): number {
    if (!iso) return 0;
    const t = Date.parse(iso);
    return Number.isFinite(t) ? Math.max(0, Math.floor((now - t) / 1000)) : 0;
}

/** Readiness as the harness writes it ({score, auto, auto_p, steps}) or a bare number. */
export function formatReadiness(r: unknown): string {
    if (typeof r === 'number' && Number.isFinite(r)) return String(r);
    if (r && typeof r === 'object') {
        const o = r as Record<string, unknown>;
        if (typeof o.score === 'number') {
            const steps = typeof o.steps === 'number' ? o.steps : null;
            const auto = typeof o.auto === 'number' ? o.auto : null;
            return steps !== null && auto !== null ? `${o.score} (${auto} of ${steps} steps automatic)` : String(o.score);
        }
    }
    return '—';
}

export function formatRunTimestamp(iso: string | null | undefined): string {
    if (!iso) return '—';
    const d = new Date(iso);
    return Number.isNaN(d.getTime()) ? String(iso) : d.toLocaleString('en-IN');
}

export interface RunStatusMeta { label: string; tone: string; running: boolean }

export function runStatusMeta(status: string | null | undefined): RunStatusMeta {
    const s = String(status || '').toLowerCase();
    if (s === 'running') return { label: 'Running', tone: 'bg-sky-500/10 text-sky-700 dark:text-sky-300 border-sky-500/30', running: true };
    if (s === 'queued') return { label: 'Queued', tone: 'bg-sky-500/10 text-sky-700 dark:text-sky-300 border-sky-500/30', running: true };
    if (s === 'succeeded') return { label: 'Succeeded', tone: 'bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 border-emerald-500/30', running: false };
    if (s === 'failed') return { label: 'Failed', tone: 'bg-red-500/10 text-red-700 dark:text-red-300 border-red-500/30', running: false };
    if (s === 'cancelled') return { label: 'Cancelled', tone: 'bg-amber-500/10 text-amber-700 dark:text-amber-300 border-amber-500/30', running: false };
    return { label: s ? s.replace(/_/g, ' ') : 'Unknown', tone: 'bg-secondary text-muted-foreground border-border', running: false };
}

/** Status shown for a run: a live job status wins over the recorded one. */
export function runDisplayStatus(run: Pick<CaseRun, 'status' | 'job_status'>): string | null {
    if (isActiveJobStatus(run.job_status)) return run.job_status;
    return run.status || run.job_status || null;
}

/** `/cases/:id/report?run_id=<event id>`; null when the run has no recorded event yet. */
export function runReportUrl(caseId: string, run: Pick<CaseRun, 'event_id'>): string | null {
    if (!run.event_id) return null;
    return `/cases/${encodeURIComponent(caseId)}/report?run_id=${encodeURIComponent(run.event_id)}`;
}

/** `/cases/:id/canvas`, with `?job=` while the run is going so the canvas watches it. */
export function runCanvasUrl(caseId: string, run: Pick<CaseRun, 'job_id' | 'status' | 'job_status'>): string {
    const base = `/cases/${encodeURIComponent(caseId)}/canvas`;
    return run.job_id && isRunActive(run) ? `${base}?job=${encodeURIComponent(run.job_id)}` : base;
}

/** Short id for display (first 8 characters). */
export function shortId(id: string | null | undefined): string {
    if (!id) return '—';
    return id.length > 8 ? id.slice(0, 8) : id;
}
